import { derived, get, readable, type Readable } from 'svelte/store';

import { real_id } from 'src/helpers/interface_helpers';

import { devices } from '../lists/devices';
import { createRecordSelector } from './store-selectors';
import { devices_settings, devices_status } from './status';

export const getDeviceStatus = createRecordSelector(devices_status, real_id);
export const getDeviceSettings = createRecordSelector(devices_settings, real_id);
export const getDevice = createRecordSelector(devices);

/** Non-reactive read — use in event handlers and async callbacks. */
export function readDeviceStatus(deviceId: string | undefined) {
	return get(getDeviceStatus(deviceId));
}

export function readDeviceSettings(deviceId: string | undefined) {
	return get(getDeviceSettings(deviceId));
}

function createMemberRecordSnapshot<TValue>(
	memberIds: string[],
	resolveSelector: (key: string) => Readable<TValue | undefined>,
): Readable<Record<string, TValue | undefined>> {
	const keys = [...new Set(memberIds.map((id) => real_id(String(id))))];

	if (keys.length === 0) {
		return readable<Record<string, TValue | undefined>>({});
	}

	const selectors = keys.map((key) => resolveSelector(key));

	return derived(selectors, (values) => {
		const snapshot: Record<string, TValue | undefined> = {};
		keys.forEach((key, index) => {
			snapshot[key] = values[index];
		});
		return snapshot;
	});
}

/** Status snapshot for group / multi-device UI — updates only when a member entry changes. */
export function createMemberStatusSnapshot(memberIds: string[]) {
	return createMemberRecordSnapshot(memberIds, (key) => getDeviceStatus(key));
}

/** Settings snapshot for group / multi-device UI — updates only when a member entry changes. */
export function createMemberSettingsSnapshot(memberIds: string[]) {
	return createMemberRecordSnapshot(memberIds, (key) => getDeviceSettings(key));
}
